import Guid from "~/helpers/guid";
import IRepo from "~/interfaces/repo";
import ProcessFlowBuilderModel from "~/models/tracked/processFlowBuilderModel";
import Repo from "~/state/modules/repo";
import {ProcessFlowStepType} from "../../enums/processFlowStepType";
import {
  ProcessFlowStepCapability,
  ProcessFlowStepCapabilityManager
} from "~/models/processFlows/processFlowStepCapabilityManager";
import {Hash} from "~/interfaces/hashInterfaces";
import ProcessFlowStepJumpTo from "./processFlowStepJumpTo"
import ProcessFlowStepElementConditionalRuleSet from "~/models/processFlows/processFlowStepElementConditionalRuleSet";
import {IProcessFlow, IProcessFlowMappedVariable, IProcessFlowStep, IProcessFlowStepAction, IProcessFlowStepConstructorOptions, IProcessFlowVariable} from '~/interfaces/processFlowInterfaces'
import {RelationType} from "~/models/relationType";
import IConditionalRuleContainer from '~/models/conditionalRules/conditionalRuleContainer'
import ConditionalRuleSet from '~/models/conditionalRules/conditionalRuleSet'
import IConditionalRuleReferencedControl from '~/models/conditionalRules/referencedControl'
import { ConditionalFieldDefinition } from '~/models/conditionalRules/conditionalFieldDefinition'
import ProcessFlowStepDefinition from "~/models/processFlows/processFlowStepDefinition";
import {ObjectHelper} from "~/helpers/objectHelper";
import ProcessFlowMappedVariable from '~/models/processFlows/steps/processFlowMappedVariable'
import IModel, {IHasParent} from '~/interfaces/modelInterfaces'
import ProcessFlowStepElementConditionalFields from '~/models/processFlows/processFlowStepElementConditionalFields'
import Validatable from '~/mixins/validatable'
import ProcessFlowStepValidation from '~/models/navigableValidations/processFlowStepValidation'
import ProcessFlowStepAction from '~/models/processFlows/processFlowStepAction'
import ProcessFlowStepActionJumpToFactory from '~/models/processFlows/processFlowStepActionJumpToFactory'

export default class ProcessFlowStep extends Validatable(ProcessFlowBuilderModel) implements IProcessFlowStep, IConditionalRuleContainer {
  step_type: ProcessFlowStepType
  title: string
  readonly _nameInParent = "steps"
  jump_to: ProcessFlowStepJumpTo;
  jump_to_conditional_rule_sets: Hash<ProcessFlowStepElementConditionalRuleSet> = {};
  jump_to_mapped_variable_references: Hash<ProcessFlowMappedVariable> = {};
  readonly JumpToFieldName = 'jump_to';
  actions: { [key: string]: ProcessFlowStepAction} = {};

  constructor(parent, options: IProcessFlowStepConstructorOptions) {
    super(options);
    this.parent = parent
    this.rootModel = parent
    this.flow = parent.flow

    this.defineProperty('client_id', Guid.create());
    this.defineProperty('title', options.defaultTitle || '');
    this.defineProperty('step_type', options.stepType)

    this.createEmbeddedObject = ProcessFlowStepDefinition.createEmbeddedDefinitionFactory(this)
    this.setupConfigurableMethods();
  }

  //******************** VALIDATABLE ********************//
  _validation: ProcessFlowStepValidation<ProcessFlowStep> = new ProcessFlowStepValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }
  //******************** VALIDATABLE END ********************//

  // ******************** RELATIONS AND REPOS ******************** //

  static repo: IRepo<ProcessFlowStep> = new Repo<ProcessFlowStep>('processFlowSteps', ProcessFlowStep, {});
  repo: IRepo<this>;

  addToRepo(): void {
    ProcessFlowStep.repo.add(this)
  }

  get collectionKey() {
    return 'steps'
  }

  get rootName(): string {
    return this._nameInParent;
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return ProcessFlowStepDefinition.embeddedModelDefinitionFactory(this.step_type);
  }

  get EMBEDDED_HASHES(): string[] {
    return [this.JumpToFieldName];
  }

  // ******************** RELATIONS AND REPOS END ******************** //

  get defaultTitle(): string {
    return ''
  }

  get titleOrDefault(): string {
    return this.title || this.defaultTitle || ''
  }

  get displayName(): string {
    return ProcessFlowStepDefinition.getDisplayName(this.step_type);
  }

  get icon(): string {
    return ProcessFlowStepDefinition.getIcon(this.step_type);
  }

  // ******************** JUMP TO ******************** //
  get nextSteps(): Array<ProcessFlowStep | ProcessFlowStepJumpTo | ProcessFlowStepAction> {
    if (this.actionsEnabled) {
      return Array.from(this.orderedActions);
    } else {
      return this.jumpTos.map(k => this.parent.steps[k.to_client_id] ? this.parent.steps[k.to_client_id] : k);
    }
  }

  get jumpTos(): ProcessFlowStepJumpTo[] {
    const values = [];
    const ruleSets = this.getSortedJumpToConditionals();

    Object.keys(ruleSets).forEach(k => values.push(ruleSets[k].conditional_value));
    values.push(this.jump_to);
    return values
  }

  getSortedJumpToConditionals(): ProcessFlowStepElementConditionalRuleSet[] {
    const ruleSets = this.getJumpToConditionals();
    return Object.values(ruleSets)
      .sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  can(capability: ProcessFlowStepCapability): boolean {
    if (this.step_type === ProcessFlowStepType.Redirect &&
      this.parent.step_type === ProcessFlowStepType.RunIteration) return false

    return ProcessFlowStepCapabilityManager.can(this.step_type, capability);
  }

  getJumpToConditionals(): Hash<ProcessFlowStepElementConditionalRuleSet> {
    return this[`${this.JumpToFieldName}_conditional_rule_sets`];
  }

  canJumpTo(clientId: string): boolean {
    return this.checkJumpToRecursively(clientId, {});
  }

  protected checkJumpToRecursively(clientId, clientIdsSoFar): boolean {
    clientIdsSoFar[this.client_id] = true;
    const actionJumpToClientId = this.jump_to.to_client_id;
    if (actionJumpToClientId === clientId) return true;
    const jumpToConditionals = this.getJumpToConditionals();
    for(const key in jumpToConditionals) {
      const jumpToClientId = jumpToConditionals[key].conditional_value.to_client_id;
      if (jumpToClientId === clientId) return true;
      if (!clientIdsSoFar[jumpToClientId] && jumpToClientId !== '' &&
        this.rootModel.steps[jumpToClientId] && this.rootModel.steps[jumpToClientId].checkJumpToRecursively(clientId, clientIdsSoFar)) {
        return true;
      }
    }
    return false;
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): ConditionalRuleSet {
    return new ProcessFlowStepElementConditionalRuleSet(fieldName, definition);
  }

  getConditionalRuleSets(fieldName: string): Hash<ConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getReferencableControls(): IConditionalRuleReferencedControl[] {
    // TODO: Variables should meet this interface. Once true, remove <any>
    return Object.values(<any>this.flow.variables);
  }

  /**
   * Look in collections of mapped variables for one with the id
   * If implementing in subclass, be sure to call super if mapped variable cannot be found
   * @param id
   */
  getReferencedControl(id: string): IConditionalRuleReferencedControl {
    return this.jump_to_mapped_variable_references[id]
  }

  getSortedConditionalRuleSets(fieldName: string): ConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order - y.sort_order);
  }
  // ******************** JUMP TO END ********************

  /**
   * @param title
   */
  initializeTitle(title: string): void {
    if (!Boolean(this.title)) {
      this.title = title
    }
  }

  get isMetadataConfigurable(): boolean {
    return ProcessFlowStepCapabilityManager.metadataConfigurable.some(capability => this.can(capability))
  }

  setupConfigurableMethods() {
    const dict = {
      isMetadataConfigurable: "metadataConfigurable",
      isGeneralConfigurable: "generalConfigurable",
      isFlowPathConfigurable: "flowPathConfigurable",
      isContextConfigurable: "contextConfigurable",
    }
    for (const key in dict) {
      const methodName = key;
      const targetProp = dict[key];
      Object.defineProperty(this, methodName, {
        get() {
          return ProcessFlowStepCapabilityManager[targetProp].some(capability => this.can(capability));
        }
      })
    }
  }

  get isDestroyable(): boolean {
    return ProcessFlowStepCapabilityManager.can(this.step_type, ProcessFlowStepCapability.destroyable)
  }

  unlinkAndDestroy(containers: Array<IProcessFlowStep | IProcessFlowStepAction> = this.parent.steps):void {
    Object.values(containers).forEach((container) => {
      if (!(container instanceof ProcessFlowStep || container instanceof ProcessFlowStepAction)) { return }

      if ('actionsEnabled' in container && container.actionsEnabled) {
        this.unlinkAndDestroy(container.actionsArray)
      } else if (container.jump_to.to_client_id === this.client_id) {
        container.jump_to.to_client_id = null
      }

      Object.values(this.jump_to_conditional_rule_sets).forEach((crsContainer) => {
        if (crsContainer.default_value === this.client_id) {
          crsContainer.default_value = null
        }

        Object.values(crsContainer.conditional_rules).forEach((crs) => {
          if (!(crs instanceof ConditionalRuleSet)) { return }

          if (crs.conditional_value.to_client_id === this.client_id) {
            crs.conditional_value.to_client_id = null
          }
        })
      })
    })

    this.destroy()
  }

  get destinationVariable(): IProcessFlowVariable {
    return null
  }

  get destinationVariableClientId(): string {
    return null
  }

  // ******************** Query Rule Builder START ******************** //
  createMappedVariableReference(fieldName: string): ProcessFlowMappedVariable {
    const reference = new ProcessFlowMappedVariable(`${fieldName}_mapped_variable_references`);
    reference.attachToParent(this);
    return reference;
  }
  // ******************** Query Rule Builder END ******************** //

  cleanUp(): void {
    this.cleanUpMappedVariableReferences()
  }

  _mappedVariableReferenceCollectionNames: string[]
  protected mappedVariableReferenceCollectionNames(...names: string[]): readonly string[] {
    if (!this._mappedVariableReferenceCollectionNames) {
      this._mappedVariableReferenceCollectionNames = names.concat(...ProcessFlowStepElementConditionalFields.PROCESS_FLOW_STEP_FIELDS.map(definition => definition.fieldName))
    }
    return this._mappedVariableReferenceCollectionNames
  }

  private cleanUpMappedVariableReferences() {
    this.mappedVariableReferenceCollectionNames().forEach(name => {
      this.cleanUpMappedVariableReferencesFor(name)
    })
  }

  /**
   * Destroy any mapped variables referenced inside places like conditional rules and rule sets
   * that are no longer being used because the places where they were reference have changed or been destroyed themselves
   *
   * @param key
   * @protected
   */
  protected cleanUpMappedVariableReferencesFor(key: string) {
    const ruleSets = Object.values(this[`${key}_conditional_rule_sets`]) as ConditionalRuleSet[]

    const referencedIds = []
    ruleSets.forEach(ruleSet => {
      ruleSet.forEachRule(rule => {
        if (rule.referenced_control_id) referencedIds.push(rule.referenced_control_id)
        if (rule.referenced_value_id) referencedIds.push(rule.referenced_value_id)
      })
    });

    (Object.values(this[`${key}_mapped_variable_references`]) as (IProcessFlowMappedVariable & IModel)[]).forEach(mv => {
      if (!referencedIds.includes(mv.client_id)) {
        mv.destroy()
      }
    })
  }

  get actionsEnabled(): boolean {
    return ProcessFlowStepCapabilityManager.can(this.step_type, ProcessFlowStepCapability.actionConfigurable)
  }

  get actionsArray(): ProcessFlowStepAction[] {
    return Object.values(this.actions)
  }

  get hasActions(): boolean {
    return this.actionsArray.length > 1
  }

  get defaultAction(): ProcessFlowStepAction {
    return this.orderedActions[this.orderedActions.length - 1]
  }

  get orderedActions(): ProcessFlowStepAction[] {
    return this.actionsArray.sort((a, b) => a.sort_order - b.sort_order);
  }

  appendAction(props, track = true): ProcessFlowStepAction {
    const action = new ProcessFlowStepAction(this, props);
    action.jump_to = ProcessFlowStepActionJumpToFactory.create(action);
    if (track) {
      this.trackCreationDeletion(action.client_id, null, action.asJson(), action);
    }
    return action;
  }

  /**
   * For common interface with ProcessFlowStepAction for linking steps and attaching to appropriate parent
   */
  initializeStep(item: IProcessFlowStep & IHasParent<IProcessFlow>, position: number = null, json: object = {}, selectItem: boolean = true) {
    if ('initializeEmbedded' in this.parent) {
      this.parent.initializeEmbedded(this.parent, item, position, json, selectItem)
    } else {
      this.parent.initializeStep(item, position, json, selectItem)
    }
  }

  /**
   * Should not be able to add Redirect step inside
   * Handles case of display a form step inside an iteration step
   */
  get canContainFinalStep(): boolean {
    return this.parent.canContainFinalStep
  }
}